// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx, ParamComponent} from 'panel';

import {FeatureGate, Permission, ProjectFeatureFlag, ReportName} from 'common/report/constants';
import {getReportSamplingFactor} from 'common/report/util';
import {SearchDisplayContext} from 'common/report/util/search/types';
import {propagateEvent} from 'common/util/dom';
import {QueryReportHeaderEl} from 'common/query-builder/query-report-header';
import {
  HeaderAction,
  HeaderCustomAlertParams,
  QueryReportHeaderParams,
  UntitledReportName,
  UntitledReportNameKey,
} from 'common/query-builder/query-report-header/types';
import {Bookmark} from 'common/report/dashboards/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {
  buildQueryReportHeaderParams,
  normalizeBookmarkEntity,
  normalizeDashboardEntity,
} from 'common/report/util/saved-entities';
import {CurrentProject} from 'common/report/model-resources/projects';

import {IronContextRegistry} from '../context-registry';
import {getFullReportUrl} from '../util/boards';
import {DashboardOrigin} from '../util/bookmark-overrides';
import {DownloadFormat, getDownloadFormatFromAction, getDownloadOptions} from '../util/report-download';
import {EntityType} from '../util/saved-entities/types';
import {BaseQueryResultsType} from '../types';

export type ReportHeaderParams = {
  appMode: boolean;
  csvExportEnabled: boolean;
  fixedCsvExportLimit?: number;
  hasUnsavedChanges: boolean;
  isQuerySamplingEnabled: boolean;
  triggerQuerySamplingTourTooltip: boolean;
  originDashboard: DashboardOrigin;
  dashboardsRoute: DashboardOrigin[];
  projectId: number;
  reportHasData: boolean;
  samplingFactor: number;
  selectedBookmarkDashboard: DashboardOrigin;
  selectedBookmarkDescription: string;
  selectedBookmarkName: string;
  isSavingBookmark: boolean;
  userId: number;
  workspaceId: number;
};

export type ReportHeaderProps<
  BookmarkParamsType = any,
  QueryResultsType extends BaseQueryResultsType = BaseQueryResultsType,
> = {
  project: CurrentProject;
  results: QueryResultsType;
  bookmark: Bookmark<BookmarkParamsType>;
  dataGroups: DataGroup[];
  showCustomAlertModal: boolean;
};

export type ReportHeaderEventHandlers = {
  'back-to-board': (ev: CustomEvent) => void;
  refresh: (ev: CustomEvent<void>) => void;
  save: (ev: CustomEvent) => void;
  delete: (ev: CustomEvent<void>) => void;
  export: (ev: CustomEvent<{format: DownloadFormat}>) => void;
  update: (ev: CustomEvent<{name?: string; description?: string}>) => void;
  move: (ev: CustomEvent) => void;
  'toggle-query-sampling': (ev: CustomEvent<{on: boolean}>) => void;
  'dismiss-query-sampling-tour': (ev: CustomEvent) => void;
};

export type ReportHeaderJsxProps = {
  params: ReportHeaderParams;
  props: ReportHeaderProps;
  on: ReportHeaderEventHandlers;
};

export abstract class ReportHeader<
  BookmarkParamsType = any,
  QueryResultsType extends BaseQueryResultsType = BaseQueryResultsType,
> extends ParamComponent<ReportHeaderParams, any, IronContextRegistry> {
  protected abstract displayContext: SearchDisplayContext;
  protected abstract reportType: ReportName;

  protected customAlertsEnabled = false;

  get defaultState() {
    return {};
  }

  get config() {
    return {
      contexts: [`i18n` as const],

      params: {
        appMode: Boolean,
        csvExportEnabled: Boolean,
        fixedCsvExportLimit: Number,
        hasUnsavedChanges: Boolean,
        isQuerySamplingEnabled: Boolean,
        triggerQuerySamplingTourTooltip: Boolean,
        originDashboard: Object,
        dashboardsRoute: Array,
        projectId: Number,
        reportHasData: Boolean,
        samplingFactor: Number,
        selectedBookmarkDashboard: Object,
        selectedBookmarkDescription: String,
        selectedBookmarkName: String,
        isSavingBookmark: Boolean,
        userId: Number,
        workspaceId: Number,
      },

      defaultState: this.defaultState,

      template: () => {
        return (
          <QueryReportHeaderEl
            attrs={{
              elref: `commonReportHeader`,
            }}
            params={{
              ...this.reportHeaderParams(),
            }}
            on={this.headerEventHandlers}
            props={{showCustomAlertModal: this._showCustomAlertModal}}
          />
        );
      },
    };
  }

  private declare _project: CurrentProject;
  get project() {
    return this._project;
  }
  set project(project: CurrentProject) {
    this._project = project;
    this.update();
  }

  private declare _results: QueryResultsType;
  get results() {
    return this._results;
  }
  set results(results: QueryResultsType) {
    this._results = results;
    this.update();
  }

  private declare _bookmark: Bookmark<BookmarkParamsType>;
  get bookmark() {
    return this._bookmark;
  }
  set bookmark(bookmark: Bookmark<BookmarkParamsType>) {
    this._bookmark = bookmark;
    this.update();
  }

  private declare _dataGroups: DataGroup[];
  get dataGroups() {
    return this._dataGroups;
  }
  set dataGroups(dataGroups: DataGroup[]) {
    this._dataGroups = dataGroups;
    this.update();
  }

  private declare _showCustomAlertModal: boolean;
  set showCustomAlertModal(val) {
    this._showCustomAlertModal = val;
    this.update();
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  reportHeaderParams(): QueryReportHeaderParams {
    const computedAtStr = this.results?.computed_at && new Date(this.results.computed_at).toISOString();
    const samplingFactor = getReportSamplingFactor(this.results);

    let customAlertParams = {};
    if (this.customAlertsEnabled) {
      if (!this.params.reportHasData) {
        customAlertParams = {
          isCustomAlertsActionVisible: true,
          customAlertsDisabledText: this.t(`reports.actions.alerts-need-data`),
          resultsKeysAndValues: [],
        };
      } else {
        customAlertParams = this.reportHeaderCustomAlertParams();
      }
    }

    const invalidMessage = this.getInvalidParamsMessage();

    return {
      ...buildQueryReportHeaderParams({
        projectId: this.params.projectId,
        entity: normalizeBookmarkEntity(this.bookmark),
        displayContext: this.displayContext,
        ...(invalidMessage ? {invalidMessage, invalidEntity: true} : {}),
        dashboardPermOverride: normalizeDashboardEntity(this.bookmark.dashboard),
      }),
      selectedBookmarkDashboard: this.params.selectedBookmarkDashboard,
      isSavingBookmark: this.params.isSavingBookmark,
      originDashboard: this.params.originDashboard,
      dashboardsRoute: this.params.dashboardsRoute,
      entityType: EntityType.Report,
      trackName: this.bookmark.type,
      workspaceId: this.params.workspaceId,
      userId: this.params.userId,
      hasUnsavedChanges: this.params.hasUnsavedChanges,
      overflowControlItems: [{items: this.getDownloadControlItems()}],
      duplicateReportUrl: this.getDuplicateReportUrl(),
      // query-sampling
      samplingFactor,
      isQuerySamplingEnabled: this.params.isQuerySamplingEnabled,
      isQuerySamplingActionVisible: this.project?.hasFeatureFlag(ProjectFeatureFlag.QTS),
      triggerQuerySamplingTourTooltip: this.params.triggerQuerySamplingTourTooltip,
      emptyEntityName: this.t(UntitledReportNameKey),
      ...(computedAtStr ? {computedAt: computedAtStr} : null),
      ...this.reportSpecificHeaderParams(),
      ...customAlertParams,
    };
  }

  getDuplicateReportUrl() {
    const title = `Duplicate of ${this.bookmark.name ?? UntitledReportName}`;
    return getFullReportUrl(this.bookmark.type, this.getStandaloneHash(title));
  }

  get headerEventHandlers() {
    return {
      'back-to-board': (ev) => propagateEvent(this, ev),
      save: (ev) => propagateEvent(this, ev),
      actionclick: (ev) => {
        if (ev.detail.value === HeaderAction.Refresh) {
          this.dispatchEvent(new CustomEvent(`refresh`));
        } else if (ev.detail.value === HeaderAction.Delete) {
          this.dispatchEvent(new CustomEvent(`delete`));
        } else if (ev.detail.value === HeaderAction.QuerySampling) {
          this.dispatchEvent(new CustomEvent(`toggle-query-sampling`, {detail: ev.detail}));
        } else if (ev.detail.value === HeaderAction.DismissQuerySamplingTour) {
          this.dispatchEvent(new CustomEvent(`dismiss-query-sampling-tour`, {detail: ev.detail}));
        } else if (ev.detail.name === `move`) {
          this.dispatchEvent(new CustomEvent(`move`, {detail: ev.detail}));
        } else {
          const format = getDownloadFormatFromAction(ev.detail.value);
          if (format) {
            this.dispatchEvent(new CustomEvent(`export`, {detail: {format}}));
          }
        }
      },
      editreport: (ev) => {
        this.dispatchEvent(new CustomEvent(`update`, {detail: ev.detail}));
      },
    };
  }

  private getDownloadControlItems() {
    const csvExportTooltip =
      this.params.csvExportEnabled && typeof this.params.fixedCsvExportLimit === `number`
        ? this.t(`reports.actions.exports.segment-limit`, {limit: this.params.fixedCsvExportLimit.toLocaleString()})
        : null;

    const downloadFormats = this.params.csvExportEnabled
      ? Object.values(DownloadFormat)
      : [DownloadFormat.PNG, DownloadFormat.PDF];

    return getDownloadOptions({
      downloadFormats,
      exportCsvDefaultTooltipConfig: csvExportTooltip ? {text: csvExportTooltip} : null,
      hasCanExportCsvFeatureGate: this.project?.getFeatureGate(FeatureGate.CanExportCsv),
      hasDownloadCsvDataPermission: this.project?.hasPermission(Permission.DownloadCSVData),
    });
  }

  // Exclude custom alert attributes so they can be supplied separately, allowing us to handle general cases
  protected reportSpecificHeaderParams(): Partial<
    QueryReportHeaderParams & {[P in keyof HeaderCustomAlertParams]: never}
  > {
    return {};
  }

  protected reportHeaderCustomAlertParams(): HeaderCustomAlertParams {
    return {};
  }

  protected abstract getInvalidParamsMessage(): string;

  /**
   * Used to open a duplicate report in a new tab to match current boards behavior.
   */
  protected abstract getStandaloneHash(titleOverride: string): string;
}
